import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CiVoteId, ciReportVoteTypes } from '../../shared/models/ci/ci-vote';
import { IBallotVoteDto } from '../../shared/models/ballot-base/ballot-vote-dto';
import { ICommitteeInternalBallotDto } from '../../shared/models/ci/committee-internal-ballot-dto';
import { IBallotCommitteeMemberDto } from '../../shared/models/ballot-base/ballot-committee-member-dto';
import { IBallotReportVoteResultsBreakdownPercentage } from '../../shared/models/reports/ballot-report';
import { CiBallotReport } from '../../shared/models/reports/ci-ballot-report';
import { appConstants } from '../../../../shared/constants/app-constants';

@Component({
    selector: 'app-cib-report',
    templateUrl: './cib-report.component.html',
    standalone: false
})
export class CibReportComponent implements OnChanges {

  @Input() ciBallotDetail: ICommitteeInternalBallotDto | null = null;

  ballotReport: CiBallotReport | undefined = undefined;

  constructor() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ciBallotDetail'] && changes['ciBallotDetail'].currentValue && this.ciBallotDetail) {

      this.ballotReport = new CiBallotReport();
      this.ballotReport.reference = this.ciBallotDetail.ballotSummary.reference;
      this.ballotReport.documentHeader = `Committee Internal Ballot - ${this.ballotReport.reference}`;
      this.ballotReport.documentFilename = `${this.ballotReport.reference} CIB Report.pdf`;
      this.ballotReport.question = this.ciBallotDetail.ballotSummary.question;
      this.ballotReport.committee = this.ciBallotDetail.ballotSummary.committeeDescription;
      this.ballotReport.projectManager = this.ciBallotDetail.ballotSummary.projectManagerName;
      this.ballotReport.associatedDocuments = [];
      if (this.ciBallotDetail.supportingDocument) {
        this.ballotReport.associatedDocuments.push(this.ciBallotDetail.supportingDocument.metadata.filename);
      }
      this.ballotReport.startDate = this.ciBallotDetail.ballotSummary.startDate ?? undefined;
      this.ballotReport.endDate = this.ciBallotDetail.ballotSummary.endDate ?? undefined;
      this.ballotReport.status = this.ciBallotDetail.ballotSummary.statusDisplayText;
      this.ballotReport.notes = this.ciBallotDetail.ballotSummary.notes;

      this.setBallotResultBreakdown();
      this.setCommitteeVotes();
      this.setQuestions();
    }
  }

  getMemberVote(member: IBallotCommitteeMemberDto): IBallotVoteDto | null{
    if (!member.votes.length) {
      return null;
    }

    return member.votes.reduce((latest, vote) => {
      return new Date(vote.lastUpdated) > new Date(latest.lastUpdated) ? vote : latest;
    }, member.votes[0]);
  }

  mapVotesToBallotReportVoteResultsBreakdownPercentages(members: IBallotCommitteeMemberDto[]): IBallotReportVoteResultsBreakdownPercentage[] {
    let agreeCount = 0;
    let disagreeCount = 0;
    let noResponseCount = members.filter(x => !x.hasVoted).length;

    members.filter(x => x.hasVoted)
      .forEach(x => {
        let vote = this.getMemberVote(x);
        if (vote && vote.voteOptionType === CiVoteId.AGREE) {
          agreeCount++;
        }
        else if (vote && vote.voteOptionType === CiVoteId.DISAGREE) {
          disagreeCount++;
        }
      });

    return [
      {
        label: ciReportVoteTypes.AGREE.label,
        fillColor: ciReportVoteTypes.AGREE.color,
        value: agreeCount,
        percentage: (agreeCount / members.length) * 100
      },
      {
        label: ciReportVoteTypes.DISAGREE.label,
        fillColor: ciReportVoteTypes.DISAGREE.color,
        value: disagreeCount,
        percentage: (disagreeCount / members.length) * 100
      },
      {
        label: ciReportVoteTypes.NO_RESPONSE.label,
        fillColor: ciReportVoteTypes.NO_RESPONSE.color,
        value: noResponseCount,
        percentage: (noResponseCount / members.length) * 100
      }
    ];
  }

  setBallotResultBreakdown(): void {
    if(this.ciBallotDetail && this.ballotReport){

      let eligibleVoters = this.ciBallotDetail.committeeMembers.filter((vote) => vote.isEligible);

      this.ballotReport.voteResultBreakdown = [
        {
          country: 'Overall',
          totalNumberOfVotes: eligibleVoters.length,
          votePercentages: this.mapVotesToBallotReportVoteResultsBreakdownPercentages(eligibleVoters)
        }
      ];
    }
  }

  setCommitteeVotes(): void {
    if(this.ciBallotDetail && this.ballotReport){
      this.ballotReport.committeeVotes = this.ciBallotDetail.committeeMembers.map(member => {
        return {
          committeeMember: member.fullName,
          organisation: member.organisation,
          role: member.role,
          country: member.country,
          votingRights: member.votingRights,
          answers: [
            member.isEligible
              ? this.getMemberVote(member)?.voteOptionTypeDisplayText ?? CiVoteId.NO_RESPONSE
              : ''
          ]
        };
      });
    }
  }

  setQuestions(): void {
    if (this.ciBallotDetail && this.ballotReport) {
        this.ballotReport.questions = [
            {
                questionNumber: 'Q1',
                question: appConstants.BallotVoteQuestions.ACTION_TO_TAKE_WITH_THIS_STANDARD,
                answerOptions: [
                    CiVoteId.AGREE,
                    CiVoteId.DISAGREE,
                    CiVoteId.NO_RESPONSE
                ],
                comments: this.ciBallotDetail.committeeMembers
                    .filter(x => x.isEligible)
                    .map(x => ({
                        commenter: x.fullName,
                        comment: this.getMemberVote(x)?.comments ?? ''
                    }))
            }
        ];
    }
}

}
